import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { FAQReviewCard } from '@/components/knowledge/faq-review-card'
import { FAQEditor } from '@/components/knowledge/faq-editor'
import { PageWrapper } from '@/components/animated/page-wrapper'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { RefreshCw, CheckCircle, BookOpen, Mail, FileText, Database, ChevronDown } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/ui'
import {
  getEnhancementFromCache,
  saveEnhancementToCache,
  isEnhancementCached,
  cleanExpiredEnhancementCache,
  getEnhancementCacheStats
} from '@/lib/enhancement-cache'

type ReviewFilter = 'review' | 'draft' | 'approved' | 'all_non_active'
type SourceType = 'all' | 'neoom' | 'email' | 'notion' | 'manual'

export function QAReview() {
  const [pendingFAQs, setPendingFAQs] = useState<any[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [editingFAQ, setEditingFAQ] = useState<any>(null)
  const [showEditor, setShowEditor] = useState(false)
  const [reviewedCount, setReviewedCount] = useState(0)
  const [statusFilter, setStatusFilter] = useState<ReviewFilter>('all_non_active')
  const [statusCounts, setStatusCounts] = useState({ review: 0, draft: 0, approved: 0, all_non_active: 0 })
  const [sourceFilter, setSourceFilter] = useState<SourceType>('all')
  const [sourceCounts, setSourceCounts] = useState({ all: 0, neoom: 0, email: 0, notion: 0, manual: 0 })
  const [prefetchingNext, setPrefetchingNext] = useState(false)
  const [cachedEnhancements, setCachedEnhancements] = useState<Set<string>>(new Set())
  const { toast } = useToast()
  const { preferences } = useUIStore()
  const turboMode = preferences.enableTurboMode

  useEffect(() => {
    loadStatusCounts()
    loadSourceCounts()
    cleanExpiredEnhancementCache() // Clean on mount
  }, [])

  useEffect(() => {
    loadPendingFAQs()
  }, [statusFilter, sourceFilter])

  // Turbo Mode: Pre-fetch enhancement for next FAQ
  useEffect(() => {
    if (!turboMode || pendingFAQs.length === 0) return

    const hasNext = currentIndex < pendingFAQs.length - 1
    if (!hasNext) return

    const nextFAQ = pendingFAQs[currentIndex + 1]
    if (!nextFAQ || !nextFAQ.id) return

    // Check if already cached
    if (isEnhancementCached(nextFAQ.id)) {
      setCachedEnhancements(prev => new Set([...prev, nextFAQ.id]))
      return
    }

    // Only pre-fetch for draft status (not review/approved)
    if (nextFAQ.status !== 'draft') return

    // Pre-fetch in background
    prefetchEnhancement(nextFAQ.id)
  }, [currentIndex, turboMode, pendingFAQs])

  const prefetchEnhancement = async (faqId: string) => {
    if (prefetchingNext) return // Prevent multiple simultaneous prefetches

    setPrefetchingNext(true)
    try {
      const response = await fetch('/api/knowledge/enhance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ faqId })
      })

      if (!response.ok) {
        // Silently skip if API not available or FAQ not enhanceable
        if (response.status === 404 || response.status === 500) {
          return
        }
        throw new Error(`API Error: ${response.status}`)
      }

      const result = await response.json()

      if (result.success) {
        saveEnhancementToCache(faqId, result)
        setCachedEnhancements(prev => new Set([...prev, faqId]))
      }
    } catch (error) {
      // Silently skip errors - pre-fetching is optional
    } finally {
      setPrefetchingNext(false)
    }
  }


  const loadStatusCounts = async () => {
    try {
      // Get counts for each status
      const { count: reviewCount } = await supabase
        .from('knowledge')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'review')

      const { count: draftCount } = await supabase
        .from('knowledge')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'draft')

      const { count: approvedCount } = await supabase
        .from('knowledge')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'approved')

      // All non-active (excluding archived!)
      const { count: nonActiveCount } = await supabase
        .from('knowledge')
        .select('*', { count: 'exact', head: true })
        .not('status', 'in', '(active,archived)')

      setStatusCounts({
        review: reviewCount || 0,
        draft: draftCount || 0,
        approved: approvedCount || 0,
        all_non_active: nonActiveCount || 0
      })
    } catch (error) {
      console.error('Error loading status counts:', error)
    }
  }

  const loadSourceCounts = async () => {
    try {
      // Get all knowledge with source info (exclude active AND archived)
      const { data: knowledge } = await supabase
        .from('knowledge')
        .select('source_uid, import_sources!left(source_type)')
        .not('status', 'in', '(active,archived)')

      // Count by source type
      const counts = { all: 0, neoom: 0, email: 0, notion: 0, manual: 0 }

      knowledge?.forEach((item: any) => {
        const sourceType = item.import_sources?.source_type
        if (sourceType && sourceType in counts) {
          counts[sourceType as keyof typeof counts]++
        }
        counts.all++
      })

      setSourceCounts(counts)
    } catch (error) {
      console.error('Error loading source counts:', error)
    }
  }

  const loadPendingFAQs = async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('knowledge')
        .select('*, import_sources!left(source_type)')

      // Apply status filter
      if (statusFilter === 'review') {
        query = query.eq('status', 'review')
      } else if (statusFilter === 'draft') {
        query = query.eq('status', 'draft')
      } else if (statusFilter === 'approved') {
        query = query.eq('status', 'approved')
      } else {
        // all_non_active: everything except active and archived
        query = query.not('status', 'in', '(active,archived)')
      }

      // Apply source type filter
      if (sourceFilter !== 'all') {
        query = query.eq('import_sources.source_type', sourceFilter)
      }

      const { data, error } = await query.order('created_at', { ascending: false })

      if (error) throw error

      setPendingFAQs(data || [])
      setCurrentIndex(0)
    } catch (error) {
      console.error('Error loading FAQs:', error)
      toast({
        title: 'Error',
        description: 'Failed to load FAQs',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const loadPendingFAQsAndRestorePosition = async (faqId: string) => {
    setLoading(true)
    try {
      let query = supabase
        .from('knowledge')
        .select('*, import_sources!left(source_type)')

      // Apply status filter
      if (statusFilter === 'review') {
        query = query.eq('status', 'review')
      } else if (statusFilter === 'draft') {
        query = query.eq('status', 'draft')
      } else if (statusFilter === 'approved') {
        query = query.eq('status', 'approved')
      } else {
        // all_non_active: everything except active and archived
        query = query.not('status', 'in', '(active,archived)')
      }

      // Apply source type filter
      if (sourceFilter !== 'all') {
        query = query.eq('import_sources.source_type', sourceFilter)
      }

      const { data, error } = await query.order('created_at', { ascending: false })

      if (error) throw error

      setPendingFAQs(data || [])

      // Find the edited FAQ in the new list
      if (faqId && data) {
        const newIndex = data.findIndex((faq: any) => faq.id === faqId)
        const foundFaq = data[newIndex]

        if (newIndex !== -1) {
          setCurrentIndex(newIndex)
        } else {
          // FAQ not in list anymore (status changed to active/archived)
          // Stay at current position or go to start if out of bounds
          setCurrentIndex(prev => Math.min(prev, Math.max(0, data.length - 1)))
        }
      } else {
        setCurrentIndex(0)
      }

      // Return the loaded data so we can use it
      return data
    } catch (error) {
      console.error('Error loading FAQs:', error)
      toast({
        title: 'Error',
        description: 'Failed to load FAQs',
        variant: 'destructive'
      })
      return null
    } finally {
      setLoading(false)
    }
  }

  const updateFAQStatus = async (faqId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('knowledge')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', faqId)

      if (error) throw error

      return true
    } catch (error) {
      console.error('Error updating FAQ status:', error)
      toast({
        title: 'Error',
        description: 'Failed to update FAQ status',
        variant: 'destructive'
      })
      return false
    }
  }

  // Removed handleApprove/handleReject - using Ampel status control instead

  const handleEdit = () => {
    const faqToEdit = pendingFAQs[currentIndex]
    setEditingFAQ(faqToEdit)
    setShowEditor(true)
  }

  const handleSaveEdit = async (updatedFAQ: any) => {
    const editedFaqId = updatedFAQ?.id || editingFAQ?.id

    // Don't close the editor immediately - keep it open while reloading

    try {
      // Reload to get fresh data
      await loadStatusCounts()
      await loadSourceCounts()

      // Reload FAQs but preserve position
      const reloadedFAQs = await loadPendingFAQsAndRestorePosition(editedFaqId)

      // Find the updated FAQ in the reloaded list and update editingFAQ
      if (reloadedFAQs) {
        const updatedFaqFromDB = reloadedFAQs.find((faq: any) => faq.id === editedFaqId)
        if (updatedFaqFromDB) {
          setEditingFAQ(updatedFaqFromDB) // Update the editor with fresh data
        } else {
          // FAQ was archived or deleted - close the editor
          setShowEditor(false)
          setEditingFAQ(null)
        }
      }
      toast({
        title: 'Gespeichert',
        description: 'FAQ erfolgreich aktualisiert',
        duration: 2000
      })
    } catch (error) {
      console.error('[TROUBLESHOOTING] handleSaveEdit failed:', error)

      // Close editor on error
      setShowEditor(false)
      setEditingFAQ(null)

      // Reload to restore correct state
      await loadPendingFAQs()

      toast({
        title: 'Fehler beim Speichern',
        description: error instanceof Error ? error.message : 'Bitte versuche es erneut',
        variant: 'destructive'
      })
    }
  }

  const moveToNext = () => {
    if (currentIndex < pendingFAQs.length - 1) {
      setCurrentIndex(prev => prev + 1)
    } else {
      // All reviewed, reload counts and list
      loadStatusCounts()
      loadSourceCounts()
      loadPendingFAQs()
    }
  }

  const moveToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1)
    }
  }

  const currentFAQ = pendingFAQs[currentIndex]

  if (loading) {
    return (
      <PageWrapper>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageWrapper>
    )
  }

  if (pendingFAQs.length === 0) {
    return (
      <PageWrapper>
        <div className="flex flex-col items-center justify-center h-96 space-y-4">
          <CheckCircle className="h-16 w-16 text-green-500" />
          <h2 className="text-2xl font-bold">All caught up!</h2>
          <p className="text-muted-foreground">No FAQs pending review</p>
          {reviewedCount > 0 && (
            <Badge variant="outline" className="text-lg px-4 py-2">
              Reviewed {reviewedCount} FAQ{reviewedCount !== 1 ? 's' : ''} this session
            </Badge>
          )}
          <Button onClick={loadPendingFAQs} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <div className="max-w-5xl mx-auto space-y-3">
        {/* Header - Single Row with Source Dropdown */}
        <div className="flex items-center justify-between pb-4">
          <h1 className="text-3xl font-light tracking-tight text-foreground">QA Review</h1>

          <div className="flex items-center gap-2">
            {/* Source Filter Dropdown */}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
                <Database className="h-4 w-4" />
                {sourceFilter === 'all' ? 'All sources' : sourceFilter === 'neoom' ? 'Neoom Wiki' : sourceFilter === 'email' ? 'Email' : 'Notion'} ({sourceCounts[sourceFilter]})
                <ChevronDown className="h-3.5 w-3.5 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {[
                { type: 'all' as SourceType, label: 'All sources', icon: Database },
                { type: 'neoom' as SourceType, label: 'Neoom Wiki', icon: BookOpen },
                { type: 'email' as SourceType, label: 'Email', icon: Mail },
                { type: 'notion' as SourceType, label: 'Notion', icon: FileText }
              ].map((source) => {
                const Icon = source.icon
                const count = sourceCounts[source.type]
                const isActive = sourceFilter === source.type

                return (
                  <DropdownMenuItem
                    key={source.type}
                    onClick={() => setSourceFilter(source.type)}
                    className={cn(isActive && 'bg-accent')}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    <span>{source.label}</span>
                    <span className="ml-auto text-xs text-muted-foreground">({count})</span>
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>

        {/* Status Filter - Single Row */}
        <div className="text-sm text-muted-foreground border-b pb-3">
          {/* Status Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { value: 'all_non_active' as ReviewFilter, label: 'All to review', count: statusCounts.all_non_active },
              { value: 'draft' as ReviewFilter, label: 'Draft', count: statusCounts.draft },
              { value: 'review' as ReviewFilter, label: 'Review', count: statusCounts.review },
              { value: 'approved' as ReviewFilter, label: 'Approved', count: statusCounts.approved }
            ].map((status, idx) => {
              const isActive = statusFilter === status.value

              return (
                <span key={status.value}>
                  {idx > 0 && <span className="mx-1">·</span>}
                  <button
                    onClick={() => setStatusFilter(status.value)}
                    className={cn(
                      'hover:text-foreground transition-colors',
                      isActive && 'font-semibold text-foreground'
                    )}
                  >
                    {status.label} {status.count}
                  </button>
                </span>
              )
            })}
          </div>
        </div>

        {/* Review Card */}
        {currentFAQ && (
          <FAQReviewCard
            faq={currentFAQ}
            onEdit={handleEdit}
            onNext={moveToNext}
            onPrevious={moveToPrevious}
            hasNext={currentIndex < pendingFAQs.length - 1}
            hasPrevious={currentIndex > 0}
            currentIndex={currentIndex}
            totalCount={pendingFAQs.length}
            onStatusChange={() => {
              setReviewedCount(prev => prev + 1)
              loadStatusCounts()
              loadSourceCounts()
            }}
          />
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit FAQ</DialogTitle>
          </DialogHeader>
          <FAQEditor
            faq={editingFAQ}
            onSave={handleSaveEdit}
            onCancel={() => {
              setShowEditor(false)
              setEditingFAQ(null)
            }}
          />
        </DialogContent>
      </Dialog>
    </PageWrapper>
  )
}
